import React, { useEffect, useState } from "react";
import { Spinner } from "react-bootstrap";
import {
  Video, Link2, Unlink, TestTube2, CheckCircle2, XCircle, Globe, Info, Clock, Server, Settings2,
} from "lucide-react";
import apiClient from "../../utils/apiClient";
import {
  AppCard, AppCardContent, AppCardHeader, AppCardTitle,
  AppButton, ConfirmModal, Callout,
} from "../ui";
import CommonToaster from "../Common/CommonToaster";
import "./settings.scss";

/**
 * ZohoMeetingSettings — Per-institute Zoho Meeting OAuth + region configuration.
 * Mirrors ZoomSettings UX so admins land in a familiar layout.
 */
const REGIONS = [
  { value: "in", label: "India (zoho.in)" },
  { value: "us", label: "US (zoho.com)" },
  { value: "eu", label: "Europe (zoho.eu)" },
  { value: "au", label: "Australia (zoho.com.au)" },
  { value: "jp", label: "Japan (zoho.jp)" },
  { value: "ca", label: "Canada (zohocloud.ca)" },
];

const ZohoMeetingSettings = () => {
  const [loading, setLoading]       = useState(true);
  const [savingRegion, setSavingRegion] = useState(false);
  const [connecting, setConnecting] = useState(false);
  const [testing, setTesting]       = useState(false);
  const [disconnecting, setDisconnecting] = useState(false);
  const [confirmDisconnect, setConfirmDisconnect] = useState(false);
  const [testResult, setTestResult] = useState(null);
  const [toast, setToast]           = useState({ show: false, message: "", type: "success" });
  const showToast = (message, type = "success") => setToast({ show: true, message, type });

  const [settings, setSettings] = useState({
    meetingProvider: "zoom",
    oauthAppConfigured: false,
    mode: "platform",
    platformAllowedByPlan: false,
    platformAccountConnected: false,
    maxConcurrentMeetings: 0,
    region: "in",
    connected: false,
    email: "",
    zuid: "",
    apiDomain: "",
    lastRecordingPollAt: null,
  });
  const [switchingMode, setSwitchingMode] = useState(false);
  const [confirmModeSwitch, setConfirmModeSwitch] = useState(null); // 'self' | 'platform' | null

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await apiClient.get("/institute/zoho-meeting-settings");
      setSettings(data);
    } catch (err) {
      showToast(err?.response?.data?.message || "Failed to load Zoho settings.", "error");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    const sp = new URLSearchParams(window.location.search);
    if (sp.get("status") === "success") showToast("Zoho Meeting connected successfully.");
    if (sp.get("status") === "error") showToast(`OAuth failed: ${sp.get("reason") || "unknown"}`, "error");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleRegionChange = async (region) => {
    setSavingRegion(true);
    try {
      await apiClient.put("/institute/zoho-meeting-settings", { region });
      setSettings((s) => ({ ...s, region }));
      showToast("Region updated.");
    } catch (err) {
      showToast(err?.response?.data?.message || "Failed to update region.", "error");
    } finally {
      setSavingRegion(false);
    }
  };

  const handleSwitchMode = async (next) => {
    setConfirmModeSwitch(null);
    setSwitchingMode(true);
    try {
      await apiClient.put("/institute/zoho-meeting-settings", { mode: next });
      showToast(next === "platform"
        ? "Switched to Platform-hosted Zoho. Classes will use the LMS-owned account."
        : "Switched to your own Zoho account. Connect it below to start hosting.");
      await load();
    } catch (err) {
      showToast(err?.response?.data?.message || "Failed to switch mode.", "error");
    } finally {
      setSwitchingMode(false);
    }
  };

  const handleConnect = async () => {
    setConnecting(true);
    try {
      const origin = encodeURIComponent(window.location.origin);
      const { data } = await apiClient.get(`/institute/zoho-meeting-settings/oauth-url?region=${settings.region}&origin=${origin}`);
      window.location.href = data.url;
    } catch (err) {
      showToast(err?.response?.data?.message || "Failed to start OAuth.", "error");
      setConnecting(false);
    }
  };

  const handleDisconnect = async () => {
    setConfirmDisconnect(false);
    setDisconnecting(true);
    try {
      await apiClient.delete("/institute/zoho-meeting-settings/disconnect");
      showToast("Zoho Meeting disconnected.");
      setTestResult(null);
      await load();
    } catch (err) {
      showToast(err?.response?.data?.message || "Disconnect failed.", "error");
    } finally {
      setDisconnecting(false);
    }
  };

  const handleTest = async () => {
    setTesting(true);
    setTestResult(null);
    try {
      const { data } = await apiClient.post("/institute/zoho-meeting-settings/test");
      setTestResult({ success: true, message: data.message || "Connection OK." });
    } catch (err) {
      // `error` carries Zoho's reason; `message` is only "Connection failed."
      setTestResult({ success: false, message: err?.response?.data?.error || err?.response?.data?.message || "Connection test failed." });
    } finally {
      setTesting(false);
    }
  };

  if (loading) {
    return (
      <div className="text-center py-5">
        <Spinner size="sm" /> Loading Zoho Meeting settings…
      </div>
    );
  }

  return (
    <div className="zoom-settings">
      {/* ─── Server Configuration Notice (BYO needs OAuth app at platform level) ─── */}
      {settings.mode === "self" && !settings.oauthAppConfigured && (
        <AppCard>
          <AppCardContent>
            <div className="zoho-mode-warn">
              <Info size={18} />
              <div>
                <strong>Zoho Meeting not configured at platform level.</strong>
                <p className="mb-0 settings-card-desc zoho-mode-warn__desc">
                  Ask your SaaS admin to enter Zoho Meeting OAuth credentials in <code>Sidebar → Zoho Meeting Config</code> before connecting.
                </p>
              </div>
            </div>
          </AppCardContent>
        </AppCard>
      )}

      {/* ─── Mode selector ─── */}
      <AppCard>
        <AppCardHeader>
          <AppCardTitle>
            <span className="settings-card-title">
              <Settings2 size={18} /> Zoho Meeting Mode
            </span>
          </AppCardTitle>
          <p className="settings-card-desc">
            Choose how your institute hosts Zoho Meeting classes.
          </p>
        </AppCardHeader>
        <AppCardContent>
          <div className="zoom-mode-selector">
            {/* Platform-hosted card */}
            <div
              className={`zoom-mode-card ${settings.mode === "platform" ? "zoom-mode-card--active" : ""}`}
              onClick={() => {
                if (switchingMode || settings.mode === "platform") return;
                if (!settings.platformAllowedByPlan) return;
                setConfirmModeSwitch("platform");
              }}
              style={{
                opacity: !settings.platformAllowedByPlan && settings.mode !== "platform" ? 0.55 : 1,
                cursor: switchingMode || settings.mode === "platform" || !settings.platformAllowedByPlan ? "not-allowed" : "pointer",
              }}
            >
              <div className="zoom-mode-card__header">
                <input type="radio" name="zoho-mode" checked={settings.mode === "platform"} readOnly />
                <strong><Server size={14} className="me-1" /> Platform-hosted (LMS Campus)</strong>
              </div>
              <p className="zoom-mode-card__desc">
                LMS Campus provides the Zoho Meeting account. No setup required.
                {!settings.platformAllowedByPlan && <> Not included in your current plan.</>}
                {settings.platformAllowedByPlan && settings.maxConcurrentMeetings > 0 && (
                  <> Up to <strong>{settings.maxConcurrentMeetings}</strong> concurrent live class
                  {settings.maxConcurrentMeetings === 1 ? "" : "es"}.</>
                )}
              </p>
            </div>
            {/* BYO card */}
            <div
              className={`zoom-mode-card ${settings.mode === "self" ? "zoom-mode-card--active" : ""}`}
              onClick={() => {
                if (switchingMode || settings.mode === "self") return;
                setConfirmModeSwitch("self");
              }}
              style={{ cursor: switchingMode || settings.mode === "self" ? "not-allowed" : "pointer" }}
            >
              <div className="zoom-mode-card__header">
                <input type="radio" name="zoho-mode" checked={settings.mode === "self"} readOnly />
                <strong><Link2 size={14} className="me-1" /> Bring your own Zoho account</strong>
              </div>
              <p className="zoom-mode-card__desc">
                Connect your own Zoho Meeting account via OAuth. Recordings & users live in your Zoho org.
              </p>
            </div>
          </div>
        </AppCardContent>
      </AppCard>

      {/* ─── PLATFORM MODE info card ─── */}
      {settings.mode === "platform" && (
        <AppCard>
          <AppCardHeader>
            <AppCardTitle>
              <span className="settings-card-title">
                <Video size={18} /> Live Classes via Platform Zoho
              </span>
            </AppCardTitle>
          </AppCardHeader>
          <AppCardContent>
            {settings.platformAccountConnected ? (
              <div className="zoom-connected-info">
                <div className="zoom-connected-info__status">
                  <CheckCircle2 size={20} className="text-success" />
                  <div>
                    <strong>Active</strong>
                    <p className="mb-0 text-muted">
                      Your institute is using the LMS Campus-managed Zoho account. Classes start with
                      a host automatically allocated from the shared pool.
                    </p>
                  </div>
                </div>
              </div>
            ) : (
              <div className="zoom-connect-prompt">
                <p className="text-muted mb-0 d-flex align-items-start gap-2">
                  <Info size={16} className="zoho-mode-info-icon" />
                  Platform Zoho service is being prepared. New live classes may fail until the SaaS
                  admin connects the platform account.
                </p>
              </div>
            )}
          </AppCardContent>
        </AppCard>
      )}

      {/* ─── BYO MODE: Region + Connection (legacy cards) ─── */}
      {settings.mode === "self" && (
        <>
          <AppCard>
            <AppCardHeader>
              <AppCardTitle>
                <span className="settings-card-title">
                  <Globe size={18} /> Data Centre Region
                </span>
              </AppCardTitle>
              <p className="settings-card-desc">
                Zoho Meeting is region-sharded — choose where your Zoho organization lives.
                {settings.connected && " (Locked while connected.)"}
              </p>
            </AppCardHeader>
            <AppCardContent>
              <div className="zoom-mode-selector">
                {REGIONS.map((r) => {
                  const disabled = settings.connected || savingRegion;
                  const active = settings.region === r.value;
                  return (
                    <div
                      key={r.value}
                      className={`zoom-mode-card ${active ? "zoom-mode-card--active" : ""}`}
                      onClick={() => {
                        if (disabled || active) return;
                        handleRegionChange(r.value);
                      }}
                      style={{ opacity: disabled ? 0.65 : 1, cursor: disabled ? "not-allowed" : "pointer" }}
                    >
                      <div className="zoom-mode-card__header">
                        <input
                          type="radio"
                          name="zoho-region"
                          checked={active}
                          readOnly
                          disabled={disabled}
                        />
                        <strong>{r.label}</strong>
                      </div>
                    </div>
                  );
                })}
              </div>
            </AppCardContent>
          </AppCard>

          <AppCard>
            <AppCardHeader>
              <AppCardTitle>
                <span className="settings-card-title">
                  <Video size={18} /> Zoho Meeting Account Connection
                </span>
              </AppCardTitle>
              <p className="settings-card-desc">
                Connect your institute's Zoho Meeting account to use it as an alternative to Zoom for live classes.
              </p>
            </AppCardHeader>
            <AppCardContent>
              {settings.connected ? (
                <>
                  <div className="zoom-connected-info">
                    <div className="zoom-connected-info__status">
                      <CheckCircle2 size={20} className="text-success" />
                      <div>
                        <strong>Zoho Meeting Connected</strong>
                        <p className="mb-0 text-muted">{settings.email || settings.zuid || "Connected"}</p>
                        {settings.zuid && (
                          <p className="mb-0 text-muted zoho-mode-mono">
                            Zoho User ID: <code>{settings.zuid}</code>
                          </p>
                        )}
                        {settings.lastRecordingPollAt && (
                          <p className="mb-0 text-muted d-flex align-items-center gap-1 zoho-mode-mono">
                            <Clock size={11} />
                            Last recording poll: {new Date(settings.lastRecordingPollAt).toLocaleString()}
                          </p>
                        )}
                      </div>
                    </div>
                    <div className="zoom-connected-info__actions">
                      <AppButton variant="outline" size="sm" onClick={handleTest} disabled={testing}>
                        {testing ? <><Spinner size="sm" className="me-1" /> Testing…</> :
                          <><TestTube2 size={14} className="me-1" /> Test Connection</>}
                      </AppButton>
                      <AppButton variant="outline-danger" size="sm" onClick={() => setConfirmDisconnect(true)} disabled={disconnecting}>
                        {disconnecting ? <><Spinner size="sm" className="me-1" /> Disconnecting…</> :
                          <><Unlink size={14} className="me-1" /> Disconnect</>}
                      </AppButton>
                    </div>
                  </div>

                  {settings.needsReconnect && (
                    <Callout
                      tone="warning"
                      className="mt-3"
                      action={
                        <AppButton size="xs" onClick={handleConnect} disabled={connecting}>
                          {connecting ? "Redirecting…" : "Reconnect"}
                        </AppButton>
                      }
                    >
                      Reconnect Zoho Meeting to grant one more permission (reading your Zoho organisation).
                      Classes cannot start on Zoho until you do.
                    </Callout>
                  )}

                  {testResult && (
                    <div className={`zoom-test-result mt-3 ${testResult.success ? "zoom-test-result--success" : "zoom-test-result--error"}`}>
                      {testResult.success ? <CheckCircle2 size={16} /> : <XCircle size={16} />}
                      {testResult.message}
                    </div>
                  )}
                </>
              ) : (
                <div className="zoom-connect-prompt">
                  <p className="text-muted mb-3">
                    No Zoho Meeting account connected. Connect to start hosting live classes via Zoho instead of Zoom.
                  </p>
                  <AppButton onClick={handleConnect} disabled={!settings.oauthAppConfigured || connecting}>
                    {connecting ? <><Spinner size="sm" className="me-1" /> Redirecting…</> :
                      <><Link2 size={16} className="me-1" /> Connect Zoho Meeting</>}
                  </AppButton>
                </div>
              )}
            </AppCardContent>
          </AppCard>
        </>
      )}

      <ConfirmModal
        show={confirmDisconnect}
        title="Disconnect Zoho Meeting?"
        message="Active classes using Zoho on this institute will be impacted. The provider will revert to Zoom for new classes."
        confirmText="Disconnect"
        variant="danger"
        onConfirm={handleDisconnect}
        onHide={() => setConfirmDisconnect(false)}
      />

      <ConfirmModal
        show={!!confirmModeSwitch}
        title={confirmModeSwitch === "platform" ? "Switch to Platform-hosted Zoho?" : "Switch to your own Zoho account?"}
        message={
          confirmModeSwitch === "platform"
            ? "Your institute will use the LMS Campus-managed Zoho account for new live classes. Existing OAuth tokens for your own Zoho account are kept and can be re-activated by switching back."
            : "You will need to connect your own Zoho Meeting account via OAuth before any new Zoho live classes can start."
        }
        confirmText="Switch"
        variant="info"
        onConfirm={() => handleSwitchMode(confirmModeSwitch)}
        onHide={() => setConfirmModeSwitch(null)}
      />

      <CommonToaster
        show={toast.show}
        message={toast.message}
        type={toast.type}
        onClose={() => setToast((t) => ({ ...t, show: false }))}
      />
    </div>
  );
};

export default ZohoMeetingSettings;
